import cx from 'classnames';

import ArrowRightIcon from '../assets/svgs/arrow-right.svg';
import styles from './arrowNavigator.module.scss';
import { AlignTypes } from './types';

const scrollIntoView = (targetId?: string) => {
  if (!targetId) return;
  const dom = document.getElementById(targetId);
  if (!dom) return;
  // GNB 때문에 올바르게 사용하려면 scroll-margin-top: 60px; 필요
  dom.scrollIntoView({ block: 'start', inline: 'start' });
};

interface BaseProps {
  scrollTargetId?: string;
  align?: AlignTypes;
  className?: string;
}

interface NavigatorProps extends BaseProps {
  onClickPrev?: () => unknown;
  onClickNext?: () => unknown;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  className?: string;
}

export const ArrowNavigator = ({
  scrollTargetId,
  align = 'left',
  onClickPrev,
  onClickNext,
  prevDisabled,
  nextDisabled,
  className,
}: NavigatorProps) => {
  return (
    <div className={cx(styles.wrapper, styles[align], className)}>
      <button
        type='button'
        className={styles.left}
        onClick={() => {
          onClickPrev?.();
          scrollIntoView(scrollTargetId);
        }}
        disabled={prevDisabled}
        aria-label='prev'
      >
        <ArrowRightIcon />
      </button>
      <button
        type='button'
        className={styles.right}
        onClick={() => {
          onClickNext?.();
          scrollIntoView(scrollTargetId);
        }}
        disabled={nextDisabled}
        aria-label='next'
      >
        <ArrowRightIcon />
      </button>
    </div>
  );
};

export interface PaginationProps extends BaseProps {
  isLoading?: boolean;
  page: number;
  per: number;
  total?: number;
  onChange: (nextPage: number) => void;
}

export const ArrowPagination = ({ isLoading, page, per, total = 0, onChange, ...baseProps }: PaginationProps) => {
  const isLastPage = total <= (page + 1) * per;

  if (isLoading) return null;
  if (page === 0 && isLastPage) return null;
  return (
    <ArrowNavigator
      onClickPrev={() => onChange(page - 1)}
      onClickNext={() => onChange(page + 1)}
      prevDisabled={page === 0}
      nextDisabled={isLastPage}
      {...baseProps}
    />
  );
};

export interface CursorPaginationProps extends BaseProps {
  isLoading?: boolean;
  hasPrev: boolean;
  hasNext: boolean;
  prevKey?: string;
  nextKey?: string;
  onChange: (params: { before?: string; after?: string }) => void;
}

export const ArrowCursorPagination = ({
  isLoading,
  hasPrev,
  hasNext,
  prevKey,
  nextKey,
  className,
  onChange,
  ...baseProps
}: CursorPaginationProps) => {
  if (isLoading) return null;
  if (!hasPrev && !hasNext) return null;
  return (
    <ArrowNavigator
      onClickPrev={() => onChange({ before: prevKey })}
      onClickNext={() => onChange({ after: nextKey })}
      prevDisabled={!hasPrev || !prevKey}
      nextDisabled={!hasNext || !nextKey}
      className={className}
      {...baseProps}
    />
  );
};
